import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface CustomColumn {
  id?: number
  key: string
  name: string
  type: 'text' | 'number' | 'date' | 'dropdown' | 'boolean'
  visible: boolean
  order: number
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
  resizable?: boolean
  required?: boolean
  defaultValue?: string
  dropdownOptions?: string[]
  isCustom: boolean
}

interface CustomColumnEditorProps {
  isOpen: boolean
  column?: CustomColumn | null
  onSave: (column: CustomColumn) => void
  onCancel: () => void
  existingKeys: string[]
}

const CustomColumnEditor: React.FC<CustomColumnEditorProps> = ({
  isOpen,
  column,
  onSave,
  onCancel,
  existingKeys
}) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<Partial<CustomColumn>>({
    key: '',
    name: '',
    type: 'text',
    visible: true,
    order: 0,
    align: 'left',
    sortable: true,
    resizable: true,
    required: false,
    defaultValue: '',
    dropdownOptions: [],
    isCustom: true
  })

  const [dropdownOptionsText, setDropdownOptionsText] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isOpen) {
      if (column) {
        // Editing existing column
        setFormData(column)
        setDropdownOptionsText(column.dropdownOptions?.join('\n') || '')
      } else {
        // Creating new column
        setFormData({
          key: '',
          name: '',
          type: 'text',
          visible: true,
          order: 0,
          align: 'left',
          sortable: true,
          resizable: true,
          required: false,
          defaultValue: '',
          dropdownOptions: [],
          isCustom: true
        })
        setDropdownOptionsText('')
      }
      setErrors({})
    }
  }, [isOpen, column])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Validate name
    if (!formData.name?.trim()) {
      newErrors.name = t('deals.columnSettings.customColumnEditor.validation.nameRequired')
    }

    // Validate key
    if (!formData.key?.trim()) {
      newErrors.key = t('deals.columnSettings.customColumnEditor.validation.keyRequired')
    } else if (!/^[a-z][a-z0-9_]*$/.test(formData.key)) {
      newErrors.key = t('deals.columnSettings.customColumnEditor.validation.keyFormat')
    } else if (!column && existingKeys.includes(formData.key)) {
      newErrors.key = t('deals.columnSettings.customColumnEditor.validation.keyExists')
    }

    // Validate dropdown options
    if (formData.type === 'dropdown') {
      const options = dropdownOptionsText
        .split('\n')
        .map(opt => opt.trim())
        .filter(opt => opt.length > 0)
      
      if (options.length < 2) {
        newErrors.dropdownOptions = t('deals.columnSettings.customColumnEditor.validation.dropdownMinOptions')
      } else if (new Set(options).size !== options.length) {
        newErrors.dropdownOptions = t('deals.columnSettings.customColumnEditor.validation.dropdownUniqueOptions')
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = () => {
    if (!validateForm()) return

    const dropdownOptions = formData.type === 'dropdown'
      ? dropdownOptionsText
          .split('\n')
          .map(opt => opt.trim())
          .filter(opt => opt.length > 0)
      : []

    const columnData: CustomColumn = {
      ...formData,
      dropdownOptions,
      key: formData.key!,
      name: formData.name!,
      type: formData.type!,
      visible: formData.visible!,
      order: formData.order!,
      align: formData.align!,
      sortable: formData.sortable!,
      resizable: formData.resizable!,
      required: formData.required!,
      isCustom: true
    }

    onSave(columnData)
  }

  const handleTypeChange = (newType: CustomColumn['type']) => {
    setFormData(prev => ({
      ...prev,
      type: newType,
      defaultValue: newType === 'boolean' ? 'false' : ''
    }))
    
    if (newType !== 'dropdown') {
      setDropdownOptionsText('')
    }
  }

  const generateKeyFromName = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .replace(/^_+|_+$/g, '')
  }

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      key: !column ? generateKeyFromName(name) : prev.key // Only auto-generate key for new columns
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {column ? t('deals.columnSettings.customColumnEditor.editTitle') : t('deals.columnSettings.customColumnEditor.addTitle')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {column ? t('deals.columnSettings.customColumnEditor.editSubtitle') : t('deals.columnSettings.customColumnEditor.addSubtitle')}
              </p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('deals.columnSettings.customColumnEditor.columnNameRequired')}
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder={t('deals.columnSettings.customColumnEditor.columnNamePlaceholder')}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('deals.columnSettings.customColumnEditor.columnKeyRequired')}
                </label>
                <input
                  type="text"
                  value={formData.key || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, key: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    errors.key ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder={t('deals.columnSettings.customColumnEditor.columnKeyPlaceholder')}
                  disabled={!!column} // Can't change key for existing columns
                />
                {errors.key && <p className="text-red-500 text-xs mt-1">{errors.key}</p>}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('deals.columnSettings.customColumnEditor.columnKeyHelp')}
                </p>
              </div>
            </div>

            {/* Column Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('deals.columnSettings.customColumnEditor.columnTypeRequired')}
              </label>
              <select
                value={formData.type || 'text'}
                onChange={(e) => handleTypeChange(e.target.value as CustomColumn['type'])}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="text">{t('deals.columnSettings.columnTypes.text')}</option>
                <option value="number">{t('deals.columnSettings.columnTypes.number')}</option>
                <option value="date">{t('deals.columnSettings.columnTypes.date')}</option>
                <option value="dropdown">{t('deals.columnSettings.columnTypes.dropdown')}</option>
                <option value="boolean">{t('deals.columnSettings.columnTypes.boolean')}</option>
              </select>
            </div>

            {/* Dropdown Options */}
            {formData.type === 'dropdown' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('deals.columnSettings.customColumnEditor.dropdownOptionsRequired')}
                </label>
                <textarea
                  value={dropdownOptionsText}
                  onChange={(e) => setDropdownOptionsText(e.target.value)}
                  rows={6}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    errors.dropdownOptions ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder={t('deals.columnSettings.customColumnEditor.dropdownOptionsPlaceholder')}
                />
                {errors.dropdownOptions && <p className="text-red-500 text-xs mt-1">{errors.dropdownOptions}</p>}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('deals.columnSettings.customColumnEditor.dropdownOptionsHelp')}
                </p>
              </div>
            )}

            {/* Default Value */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('deals.columnSettings.customColumnEditor.defaultValue')}
              </label>
              {formData.type === 'boolean' ? (
                <select
                  value={formData.defaultValue || 'false'}
                  onChange={(e) => setFormData(prev => ({ ...prev, defaultValue: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="false">{t('deals.columnSettings.customColumnEditor.no')}</option>
                  <option value="true">{t('deals.columnSettings.customColumnEditor.yes')}</option>
                </select>
              ) : formData.type === 'dropdown' ? (
                <select
                  value={formData.defaultValue || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, defaultValue: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('deals.columnSettings.customColumnEditor.noDefault')}</option>
                  {dropdownOptionsText
                    .split('\n')
                    .map(opt => opt.trim())
                    .filter(opt => opt.length > 0)
                    .map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                </select>
              ) : (
                <input
                  type={formData.type === 'number' ? 'number' : formData.type === 'date' ? 'date' : 'text'}
                  value={formData.defaultValue || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, defaultValue: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('deals.columnSettings.customColumnEditor.defaultValuePlaceholder')}
                />
              )}
            </div>

            {/* Display Settings */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('deals.columnSettings.customColumnEditor.textAlignment')}
                </label>
                <select
                  value={formData.align || 'left'}
                  onChange={(e) => setFormData(prev => ({ ...prev, align: e.target.value as 'left' | 'center' | 'right' }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="left">{t('deals.columnSettings.left')}</option>
                  <option value="center">{t('deals.columnSettings.center')}</option>
                  <option value="right">{t('deals.columnSettings.right')}</option>
                </select>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.visible || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, visible: e.target.checked }))}
                    className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('deals.columnSettings.customColumnEditor.visible')}</span>
                </label>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.required || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                    className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('deals.columnSettings.customColumnEditor.required')}</span>
                </label>
              </div>
            </div>

            {/* Advanced Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.sortable || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, sortable: e.target.checked }))}
                    className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('deals.columnSettings.customColumnEditor.sortable')}</span>
                </label>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.resizable || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, resizable: e.target.checked }))}
                    className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('deals.columnSettings.customColumnEditor.resizable')}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {t('deals.columnSettings.customColumnEditor.cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
          >
            {column ? t('deals.columnSettings.customColumnEditor.saveChanges') : t('deals.columnSettings.customColumnEditor.createColumn')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CustomColumnEditor